/**
 * Log a failed server READ at the right severity — the single triage for every reader.
 *
 * When the network drops (a Mac waking from sleep, Wi-Fi switching), every reader on screen fails
 * at once with « Failed to fetch » / « Load failed ». That is a cut, not a bug: the reader keeps
 * what it showed and re-reads when the network is back (`startPeriodicRead`, `createRealtimeSync`).
 * So a transient network failure is a breadcrumb (`logInfo`).
 *
 * Anything else came from the server — RLS refusal (42501), 5xx, a malformed query — and a
 * reconnect will not fix it: it keeps its warning, with its code so the cause is readable.
 */
import { logInfo, logWarn, type LogScope } from '../logger';
import { errMessage, isTransientNetworkError } from './transientError';

type LogContext = Readonly<Record<string, unknown>>;

/** The PostgREST / SQL code of an error-like value (`42501`, `PGRST116`…), when it has one. */
function errCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code: unknown }).code;
    if (typeof code === 'string' && code !== '') return code;
  }
  return undefined;
}

/**
 * A read that failed, thrown with the sentence to show (« Envois programmés illisibles. ») while
 * keeping the server's error as `reason`: the reader that catches it logs once, with the cause.
 */
export class ReadFailure extends Error {
  constructor(
    userMessage: string,
    readonly reason: unknown,
  ) {
    super(userMessage);
    this.name = 'ReadFailure';
  }
}

/**
 * @param scope   Logger scope of the surface (`travail`, `relations`…).
 * @param label   What could not be read, e.g. `'get_otto_now unreadable'`.
 * @param err     The caught error or the `error` of a Supabase answer (a {@link ReadFailure} is
 *                logged by its `reason`).
 * @param context Extra tags (`{ threadId }`…); `message` and `code` are added.
 */
export function logReadFailure(scope: LogScope, label: string, err: unknown, context?: LogContext): void {
  const cause = err instanceof ReadFailure ? err.reason : err;
  const code = errCode(cause);
  const payload = { ...context, message: errMessage(cause), ...(code ? { code } : {}) };
  if (isTransientNetworkError(cause)) {
    logInfo(scope, `${label} (transient)`, payload);
  } else {
    logWarn(scope, label, payload);
  }
}
